import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createRoute, patchRoute, type SavedRoute } from './api';
import { parseKm } from './calc';

/** A saved route's fields: from, to and the one-way km. Creates, or changes `route`. */
export function RouteForm({
  route,
  onSaved,
  onCancel,
}: {
  readonly route?: SavedRoute;
  readonly onSaved: (route: SavedRoute) => void;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation('expenses');
  const [from, setFrom] = useState(route?.from ?? '');
  const [to, setTo] = useState(route?.to ?? '');
  const [km, setKm] = useState(route?.km.replace(/\.0$/, '') ?? '');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const valid = from.trim() !== '' && to.trim() !== '' && parseKm(km) !== null;

  const save = async (): Promise<void> => {
    if (!valid || busy) return;
    setBusy(true);
    setFailed(false);
    const body = { from: from.trim(), to: to.trim(), km: parseKm(km) as string };
    const outcome = route ? await patchRoute(route.id, body) : await createRoute(body);
    setBusy(false);
    if (outcome.ok) onSaved(outcome.value);
    else setFailed(true);
  };
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <label className="flex flex-col gap-1 text-sm font-semibold">
        {t('mileage.trip.from')}
        <Input value={from} maxLength={200} onChange={(event) => setFrom(event.target.value)} />
      </label>
      <label className="flex flex-col gap-1 text-sm font-semibold">
        {t('mileage.trip.to')}
        <Input value={to} maxLength={200} onChange={(event) => setTo(event.target.value)} />
      </label>
      <label className="flex flex-col gap-1 text-sm font-semibold">
        {t('mileage.routes.km')}
        <Input
          inputMode="decimal"
          autoComplete="off"
          value={km}
          onChange={(event) => setKm(event.target.value)}
        />
      </label>
      {failed ? (
        <p role="alert" className="text-destructive text-sm">
          {t('mileage.routes.failed')}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={!valid || busy}>
          {t('mileage.routes.save')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('mileage.cancel')}
        </Button>
      </div>
    </form>
  );
}
